import { useState, useMemo } from "react";
import { ArrowLeftRight, ExternalLink as LinkIcon, Info } from "lucide-react";
import SEO from "../ui/SEO.jsx";
import ShareButtons from "../ui/ShareButtons.jsx";
import { Card, Label, YenInput, Eyebrow, Disclaimer } from "../ui/primitives.jsx";
import { useFxRates } from "../lib/useFxRates.jsx";
import { yen, brl } from "../lib/format.js";

/* ============================================================
   Remittance comparator
   ============================================================
   IMPORTANT: rates below are illustrative AVERAGES. Real rates 
   change daily and vary by amount/destination. To make this 
   actually useful in production:

   1. Wire up the real Wise API (free tier available) for 
      live mid-market rate and Wise's quote.
   2. For other services, scrape or use whatever public 
      quote endpoint exists, or maintain a manually-updated 
      JSON cached via a Netlify function (weekly refresh).
   3. Sign up as Wise affiliate — they have a real program with 
      decent CPA. Replace the wise link with your tracking link.
   
   The MARGIN values below represent how much each service 
   shaves off the mid-market rate (FX spread + hidden margin).
   Lower = better deal. These are reasonable defaults but you 
   should validate against current real quotes.
   ============================================================ */

const SERVICES = [
  {
    id: "wise",
    name: "Wise",
    fxMarginPct: 0.0045, // ~0.45% spread above mid-market
    feeFixedJPY: 600,    // simplified fixed fee
    feePctJPY: 0.006,    // ~0.6% of amount
    notes: "Câmbio próximo do mid-market. Transferência geralmente em 1 dia útil.",
    affiliateUrl: "https://wise.com/invite/u/example", // TODO: replace with your affiliate
    color: "#37517E",
  },
  {
    id: "brastel",
    name: "Brastel Remit",
    fxMarginPct: 0.015,  // ~1.5% spread
    feeFixedJPY: 880,
    feePctJPY: 0,
    notes: "Forte presença na comunidade brasileira. Atendimento em português.",
    affiliateUrl: "https://www.brastelremit.jp/",
    color: "#00563F",
  },
  {
    id: "goremit",
    name: "GoRemit (SMBC)",
    fxMarginPct: 0.022,
    feeFixedJPY: 2000,
    feePctJPY: 0,
    notes: "Banco tradicional. Mais caro mas seguro para valores altos.",
    affiliateUrl: "https://www.smbctb.co.jp/en/personal_services/remittance_to_overseas/goremit.html",
    color: "#0E5C8C",
  },
  {
    id: "bank",
    name: "Banco tradicional",
    fxMarginPct: 0.035,
    feeFixedJPY: 4500,
    feePctJPY: 0,
    notes: "Bancos japoneses (MUFG, SMBC, Mizuho). Cobram caro pela transferência internacional.",
    affiliateUrl: null,
    color: "#7A6A5C",
  },
];

function quote(service, amountJPY, midMarketRate) {
  const fixedFee = service.feeFixedJPY;
  const pctFee = amountJPY * service.feePctJPY;
  const totalFee = fixedFee + pctFee;
  const amountAfterFee = amountJPY - totalFee;
  const effectiveRate = midMarketRate * (1 - service.fxMarginPct);
  const receivedBRL = amountAfterFee * effectiveRate;
  return {
    fee: totalFee,
    effectiveRate,
    receivedBRL,
    spreadCostJPY: amountAfterFee * midMarketRate - receivedBRL / midMarketRate * midMarketRate, // approximate
  };
}

export default function RemessasCalc() {
  const { rates } = useFxRates();
  const [amount, setAmount] = useState(100000);
  const midRate = rates.JPY_TO_BRL;

  const quotes = useMemo(
    () =>
      SERVICES.map((s) => ({
        service: s,
        ...quote(s, amount, midRate),
      })).sort((a, b) => b.receivedBRL - a.receivedBRL),
    [amount, midRate]
  );

  const best = quotes[0];
  const worst = quotes[quotes.length - 1];
  const difference = best.receivedBRL - worst.receivedBRL;

  return (
    <div>
      <SEO
        title="Comparador de remessas Japão-Brasil"
        description="Compare Wise, Brastel, GoRemit e bancos. Veja quem entrega mais reais por iene em 2026."
        path="/calculadoras/remessas"
      />

      <Eyebrow>Comparador</Eyebrow>
      <h1 className="font-serif font-medium text-[40px] md:text-[56px] leading-[1.05] tracking-tight mb-5">
        Quem realmente entrega mais{" "}
        <span className="italic text-accent">reais</span>?
      </h1>
      <p className="text-base md:text-[17px] leading-relaxed text-ink-soft max-w-xl mb-10">
        Digite o valor que quer mandar e veja o ranking dos serviços de
        remessa. Considera câmbio efetivo e tarifa fixa.
      </p>

      <Card className="mb-6">
        <Label num="01">Quanto quer mandar?</Label>
        <YenInput value={amount} onChange={setAmount} />
        <div className="mt-3 text-sm text-ink-soft font-mono">
          ≈ {brl(amount * midRate)} ao câmbio mid-market
        </div>
      </Card>

      {/* Best vs worst alert */}
      <Card variant="accent" className="mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-ink-soft mb-2 font-semibold">
          <ArrowLeftRight size={14} />
          Diferença entre melhor e pior
        </div>
        <div className="font-serif text-3xl font-medium text-ink mb-1">
          {brl(difference)}
        </div>
        <p className="text-sm text-ink-soft leading-relaxed">
          Escolhendo <strong>{best.service.name}</strong> em vez de{" "}
          <strong>{worst.service.name}</strong>, sua família recebe{" "}
          {brl(difference)} a mais com a mesma remessa de {yen(amount)}.
        </p>
      </Card>

      {/* Ranked list */}
      <div className="space-y-3 mb-6">
        {quotes.map((q, i) => (
          <ServiceRow key={q.service.id} q={q} rank={i + 1} isBest={i === 0} />
        ))}
      </div>

      <ShareButtons
        url="/calculadoras/remessas"
        title="Comparador de remessas Japão-Brasil"
      />

      <Disclaimer>
        <strong className="text-ink">Aviso:</strong> taxas e spreads usados
        nesta comparação são valores aproximados de referência. Câmbio
        muda diariamente. Antes de mandar um valor alto, confirme a cotação
        no app ou site de cada serviço. Os links levam ao site oficial — em
        alguns casos podem ser links de afiliado (que ajudam a manter este
        site sem custo extra pra você).
      </Disclaimer>
    </div>
  );
}

function ServiceRow({ q, rank, isBest }) {
  const { service, fee, effectiveRate, receivedBRL } = q;
  return (
    <div
      className={`rounded-2xl p-6 transition-colors ${
        isBest
          ? "bg-surface border-2 border-success"
          : "bg-surface border border-border"
      }`}
    >
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
            style={{ backgroundColor: service.color }}
          >
            {rank}
          </div>
          <div>
            <h3 className="font-serif text-xl font-medium leading-tight">
              {service.name}
            </h3>
            {isBest && (
              <span className="text-[11px] font-mono uppercase tracking-wider text-success">
                Melhor opção
              </span>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="font-serif text-2xl font-medium tabular-nums">
            {brl(receivedBRL)}
          </div>
          <div className="text-[11px] font-mono text-ink-soft">
            chega no destino
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-soft font-mono mb-0.5">
            Câmbio efetivo
          </div>
          <div className="font-mono text-ink">
            1¥ = R$ {effectiveRate.toFixed(5)}
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-soft font-mono mb-0.5">
            Tarifa total
          </div>
          <div className="font-mono text-ink">{yen(fee)}</div>
        </div>
      </div>
      <p className="text-[13px] text-ink-soft leading-relaxed mb-3">
        {service.notes}
      </p>
      {service.affiliateUrl && (
        <a
          href={service.affiliateUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-accent text-sm font-medium no-underline hover:underline"
        >
          Ver no {service.name}
          <LinkIcon size={14} />
        </a>
      )}
    </div>
  );
}
